<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '../stores/auth'
import PaymentDocumentCard from '../components/PaymentDocumentCard.vue'
import PaymentWarningBanner from '../components/PaymentWarningBanner.vue'
import PaymentMethodCard from '../components/PaymentMethodCard.vue'
import PaymentProviderModal from '../components/PaymentProviderModal.vue'
import paymentDocumentService, { type PaymentDocument, type CheckRequirementsResponse } from '../services/payment-document.service'
import paymentMethodService, { type PaymentMethod, type PaymentProvider } from '../services/payment-method.service'

const router = useRouter()
const route = useRoute()
const { t, locale } = useI18n()
const authStore = useAuthStore()

const clubUuid = route.params.uuid as string
const isDemo = computed(() => authStore.isDemo)

// Data
const termsDocument = ref<PaymentDocument | null>(null)
const privacyDocument = ref<PaymentDocument | null>(null)
const otherDocuments = ref<PaymentDocument[]>([])
const requirements = ref<CheckRequirementsResponse | null>(null)

const loading = ref(true)
const error = ref<string | null>(null)

// Payment methods
const paymentMethod = ref<PaymentMethod | null>(null)
const showProviderModal = ref(false)

// Delete document modal
const showDeleteDocumentModal = ref(false)
const documentToDelete = ref<PaymentDocument | null>(null)
const deletingDocument = ref(false)

// Delete payment method modal
const showDeletePaymentMethodModal = ref(false)
const deletingPaymentMethod = ref(false)

// Computed
const currentRole = computed(() => authStore.initData?.persons?.current?.role || null)

const canEdit = computed(() => {
  // Check if user has permission to edit
  if (isDemo.value) return false
  return true // TODO: Add proper permission check
})

const showWarning = computed(() => {
  return requirements.value && !requirements.value.can_add_payment_method
})

const warningMessage = computed(() => {
  if (!requirements.value) return ''

  const missing: string[] = []
  if (!requirements.value.has_terms) missing.push(t('paymentSettings.missingTerms'))
  if (!requirements.value.has_privacy) missing.push(t('paymentSettings.missingPrivacy'))

  return t('paymentSettings.warningMessage', { missing: missing.join(` ${t('register.and')} `) })
})

const documentToDeleteName = computed(() => {
  if (!documentToDelete.value) return ''

  return documentToDelete.value.type === 'terms' ? t('paymentSettings.termsTitle') :
         documentToDelete.value.type === 'privacy' ? t('paymentSettings.privacyTitle') :
         documentToDelete.value.name || t('paymentSettings.document')
})

const paymentMethodName = computed(() => {
  if (!paymentMethod.value) return ''
  return paymentMethodService.getProviderName(paymentMethod.value.provider)
})

// Methods
const loadDocuments = async () => {
  loading.value = true
  error.value = null

  try {
    // Load all documents and payment method
    const [termsResponse, privacyResponse, otherResponse, requirementsResponse] = await Promise.all([
      paymentDocumentService.getDocuments(clubUuid, 'terms'),
      paymentDocumentService.getDocuments(clubUuid, 'privacy'),
      paymentDocumentService.getDocuments(clubUuid, 'other'),
      paymentDocumentService.checkRequirements(clubUuid)
    ])

    termsDocument.value = termsResponse.documents.find(d => d.is_active) || null
    privacyDocument.value = privacyResponse.documents.find(d => d.is_active) || null
    otherDocuments.value = otherResponse.documents.filter(d => d.is_active)
    requirements.value = requirementsResponse

    // Load payment method if requirements are met
    if (requirementsResponse.can_add_payment_method) {
      try {
        const methodResponse = await paymentMethodService.getPaymentMethod(clubUuid)
        paymentMethod.value = methodResponse.payment_method
      } catch (err: any) {
        // No payment method configured yet - this is ok
        if (err.response?.status === 404) {
          paymentMethod.value = null
        } else {
          console.error('Error loading payment method:', err)
        }
      }
    } else {
      paymentMethod.value = null
    }
  } catch (err: any) {
    console.error('Error loading documents:', err)
    error.value = err.response?.data?.message || t('paymentSettings.errorLoadingDocuments')
  } finally {
    loading.value = false
  }
}

const goBack = () => {
  router.push('/payments')
}

// Document handlers
const handleAddDocument = (type: 'terms' | 'privacy' | 'other') => {
  router.push(`/club/${clubUuid}/payment-documents/${type}/create`)
}

const handleEditDocument = (document: PaymentDocument) => {
  router.push(`/club/${clubUuid}/payment-documents/${document.uuid}/edit`)
}

const handleDeleteDocument = (document: PaymentDocument) => {
  documentToDelete.value = document
  showDeleteDocumentModal.value = true
}

const closeDeleteDocumentModal = () => {
  showDeleteDocumentModal.value = false
  documentToDelete.value = null
  error.value = null
}

const confirmDeleteDocument = async () => {
  if (!documentToDelete.value) return

  deletingDocument.value = true

  try {
    await paymentDocumentService.deleteDocument(clubUuid, documentToDelete.value.uuid)
    await loadDocuments() // Reload documents
    closeDeleteDocumentModal()
  } catch (err: any) {
    console.error('Error deleting document:', err)
    // Keep modal open and show error
    error.value = err.response?.data?.message || t('paymentSettings.errorDeletingDocument')
  } finally {
    deletingDocument.value = false
  }
}

const handleViewHistory = (type: 'terms' | 'privacy') => {
  router.push(`/club/${clubUuid}/payment-documents/history/${type}`)
}

// Payment method handlers
const handleShowProviderModal = () => {
  showProviderModal.value = true
}

const handleCloseProviderModal = () => {
  showProviderModal.value = false
}

const handleSelectProvider = (provider: PaymentProvider) => {
  showProviderModal.value = false
  router.push(`/club/${clubUuid}/payment-methods/create?provider=${provider}`)
}

const handleEditPaymentMethod = () => {
  router.push(`/club/${clubUuid}/payment-methods/edit`)
}

const handleDeletePaymentMethod = () => {
  showDeletePaymentMethodModal.value = true
}

const closeDeletePaymentMethodModal = () => {
  showDeletePaymentMethodModal.value = false
  error.value = null
}

const confirmDeletePaymentMethod = async () => {
  if (!paymentMethod.value) return

  deletingPaymentMethod.value = true

  try {
    await paymentMethodService.deletePaymentMethod(clubUuid)
    await loadDocuments() // Reload all data
    closeDeletePaymentMethodModal()
  } catch (err: any) {
    console.error('Error deleting payment method:', err)
    // Keep modal open and show error
    error.value = err.response?.data?.message || t('paymentSettings.errorDeletingPaymentMethod')
  } finally {
    deletingPaymentMethod.value = false
  }
}

// Lifecycle
onMounted(() => {
  loadDocuments()
})
</script>

<template>
  <main class="bg-gray-50 min-h-screen">
    <div class="w-full px-4 py-4 md:py-6">
      <!-- Back Button -->
      <div class="mb-4">
        <button
          @click="goBack"
          class="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 hover:underline"
        >
          <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clip-rule="evenodd"/>
          </svg>
          {{ t('common.backToPayments') }}
        </button>
      </div>

      <!-- Page Header -->
      <div class="mb-6">
        <h1 class="text-2xl font-bold text-gray-900">{{ t('paymentSettings.title') }}</h1>
      </div>

      <!-- Demo restriction banner -->
      <div v-if="isDemo" class="mb-6 bg-amber-50 border-2 border-amber-300 rounded p-4">
        <p class="text-sm font-semibold text-amber-800">{{ t('demo.restrictions.paymentSettingsTitle') }}</p>
        <p class="text-sm text-amber-700 mt-1">{{ t('demo.restrictions.paymentSettingsText') }}</p>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="bg-white border-2 border-gray-300 rounded shadow-sm p-6">
        <div class="flex items-center justify-center py-12">
          <div class="text-center">
            <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p class="text-gray-600">{{ t('paymentSettings.loading') }}</p>
          </div>
        </div>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4 mb-6">
        <p class="text-sm">{{ error }}</p>
        <button
          @click="loadDocuments"
          class="mt-3 px-4 py-2 bg-red-600 text-white text-sm rounded hover:bg-red-700"
        >
          {{ t('paymentSettings.retry') }}
        </button>
      </div>

      <!-- Content -->
      <template v-else>
        <!-- Documents Section -->
        <div class="bg-white border-2 border-gray-300 rounded shadow-sm p-6 mb-6">
          <h2 class="text-xl font-semibold text-gray-900 mb-4">{{ t('paymentSettings.documents') }}</h2>

          <!-- Terms of Payment -->
          <PaymentDocumentCard
            :document="termsDocument"
            :title="t('paymentSettings.termsTitle')"
            type="terms"
            :can-edit="canEdit"
            @add="handleAddDocument('terms')"
            @edit="handleEditDocument"
            @delete="handleDeleteDocument"
            @view-history="handleViewHistory"
          />

          <!-- Privacy Policy -->
          <PaymentDocumentCard
            :document="privacyDocument"
            :title="t('paymentSettings.privacyTitle')"
            type="privacy"
            :can-edit="canEdit"
            @add="handleAddDocument('privacy')"
            @edit="handleEditDocument"
            @delete="handleDeleteDocument"
            @view-history="handleViewHistory"
          />

          <!-- Other Documents -->
          <div class="mb-4 pb-4 border-b border-gray-200 last:border-b-0 last:mb-0 last:pb-0">
            <div class="flex items-start justify-between mb-3">
              <div class="flex-1">
                <h3 class="text-base font-medium text-gray-900">{{ t('paymentSettings.otherTitle') }}</h3>
                <p v-if="otherDocuments.length === 0" class="text-sm text-gray-500 mt-1">
                  {{ t('paymentSettings.noDocuments') }}
                </p>
              </div>
              <button
                v-if="canEdit"
                @click="handleAddDocument('other')"
                class="px-4 py-2 bg-blue-600 text-sm font-medium text-white rounded hover:bg-blue-700 transition-colors"
              >
                {{ t('paymentSettings.addDocument') }}
              </button>
            </div>

            <!-- List of other documents -->
            <div v-if="otherDocuments.length > 0" class="space-y-3 mt-3">
              <div
                v-for="doc in otherDocuments"
                :key="doc.uuid"
                class="border border-gray-200 rounded p-3 hover:border-blue-300 transition-colors"
              >
                <div class="flex items-center justify-between">
                  <div>
                    <p class="text-sm font-medium text-gray-900">{{ doc.name || doc.title }}</p>
                    <p class="text-xs text-gray-500 mt-1">
                      {{ t('paymentSettings.lastUpdated') }} {{ new Date(doc.updated_at).toLocaleDateString(({ pl: 'pl-PL', en: 'en-US', fr: 'fr-FR', es: 'es-ES', de: 'de-DE', it: 'it-IT', pt: 'pt-PT', cs: 'cs-CZ', nl: 'nl-NL', sv: 'sv-SE', da: 'da-DK', hu: 'hu-HU', ro: 'ro-RO', no: 'nb-NO', lt: 'lt-LT', hr: 'hr-HR', fi: 'fi-FI' }[locale] || 'en-US')) }}
                    </p>
                  </div>
                  <div class="flex gap-2">
                    <button
                      @click="handleEditDocument(doc)"
                      class="px-3 py-1 bg-gray-100 text-xs font-medium text-gray-700 rounded hover:bg-gray-200 border border-gray-300"
                    >
                      {{ t('common.edit') }}
                    </button>
                    <button
                      @click="handleDeleteDocument(doc)"
                      class="px-3 py-1 bg-red-600 text-xs font-medium text-white rounded hover:bg-red-700"
                    >
                      {{ t('common.delete') }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Payment Methods Section -->
        <div class="bg-white border-2 border-gray-300 rounded shadow-sm p-6">
          <div class="flex items-center justify-between mb-4">
            <h2 class="text-xl font-semibold text-gray-900">{{ t('paymentSettings.paymentMethods') }}</h2>
          </div>

          <!-- Warning if requirements not met -->
          <PaymentWarningBanner
            v-if="showWarning"
            :message="warningMessage"
          />

          <!-- Add Payment Method Button (only if requirements are met and no method exists) -->
          <div v-if="!showWarning && !paymentMethod && !isDemo" class="mb-4">
            <button
              @click="handleShowProviderModal"
              class="px-4 py-2 bg-green-600 text-sm font-medium text-white rounded hover:bg-green-700 transition-colors"
            >
              {{ t('paymentSettings.addPaymentMethod') }}
            </button>
          </div>

          <!-- No Payment Methods State -->
          <div v-if="!paymentMethod && !showWarning" class="text-center py-8">
            <p class="text-gray-500">{{ t('paymentSettings.noPaymentMethods') }}</p>
            <p class="text-sm text-gray-400 mt-1">{{ t('paymentSettings.noPaymentMethodsHint') }}</p>
          </div>

          <!-- Payment Method Card -->
          <PaymentMethodCard
            v-if="paymentMethod"
            :payment-method="paymentMethod"
            :can-edit="canEdit"
            @edit="handleEditPaymentMethod"
            @delete="handleDeletePaymentMethod"
          />
        </div>
      </template>
    </div>

    <!-- Delete Document Confirmation Modal -->
    <div
      v-if="showDeleteDocumentModal"
      class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      @click.self="closeDeleteDocumentModal"
    >
      <div class="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div class="p-6">
          <h2 class="text-xl font-bold text-gray-900 mb-4">{{ t('paymentSettings.deleteDocumentTitle') }}</h2>
          <p class="text-gray-700 mb-6">
            {{ t('paymentSettings.deleteDocumentConfirm', { name: documentToDeleteName }) }}
          </p>
          <div v-if="error" class="mb-4 bg-red-50 border border-red-200 text-red-800 rounded-lg p-3">
            <p class="text-sm">{{ error }}</p>
          </div>
          <div class="flex gap-3 justify-end">
            <button
              @click="closeDeleteDocumentModal"
              :disabled="deletingDocument"
              class="px-4 py-2 bg-gray-100 text-sm font-medium text-gray-700 rounded hover:bg-gray-200 border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {{ t('common.cancel') }}
            </button>
            <button
              @click="confirmDeleteDocument"
              :disabled="deletingDocument"
              class="px-4 py-2 bg-red-600 text-sm font-medium text-white rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {{ deletingDocument ? t('paymentSettings.deleting') : t('common.delete') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Payment Method Confirmation Modal -->
    <div
      v-if="showDeletePaymentMethodModal"
      class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      @click.self="closeDeletePaymentMethodModal"
    >
      <div class="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div class="p-6">
          <h2 class="text-xl font-bold text-gray-900 mb-4">{{ t('paymentSettings.deletePaymentMethodTitle') }}</h2>
          <p class="text-gray-700 mb-6">
            {{ t('paymentSettings.deletePaymentMethodConfirm', { name: paymentMethodName }) }}
          </p>
          <div v-if="error" class="mb-4 bg-red-50 border border-red-200 text-red-800 rounded-lg p-3">
            <p class="text-sm">{{ error }}</p>
          </div>
          <div class="flex gap-3 justify-end">
            <button
              @click="closeDeletePaymentMethodModal"
              :disabled="deletingPaymentMethod"
              class="px-4 py-2 bg-gray-100 text-sm font-medium text-gray-700 rounded hover:bg-gray-200 border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {{ t('common.cancel') }}
            </button>
            <button
              @click="confirmDeletePaymentMethod"
              :disabled="deletingPaymentMethod"
              class="px-4 py-2 bg-red-600 text-sm font-medium text-white rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {{ deletingPaymentMethod ? t('paymentSettings.deleting') : t('common.delete') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Provider Selection Modal -->
    <PaymentProviderModal
      :show="showProviderModal"
      @close="handleCloseProviderModal"
      @select="handleSelectProvider"
    />
  </main>
</template>
